import React, { useEffect } from "react";
import DetalleCard from "~business/Cards/DetalleCard";
import moment from "moment";

import ReceiptIcon from "@mui/icons-material/Receipt";
import { useSelector, useDispatch } from "react-redux";
import { setCuota } from "../actions";
import ItemCuotaCard from "~business/Cards/ItemCuotaCard";
import type { Cuota } from "~business/Cards/types";
import {
  CuotaRenovacion,
  CuotasRenovacion,
  FechaRenovacion,
} from "../../types";

const title = "Cuotas";

interface CuotasCardProps {
  isLoading: boolean;
  editable?: boolean;
  displayError?: boolean;
  onBlur?: () => void;
  /** La pantalla las pasa, pero la card las lee del store. */
  cuotas?: CuotasRenovacion;
}

/**
 * `vigenciaDesde` no lo devuelve el selector: el vencimiento de las cuotas se
 * calcula desde hoy.
 */
interface SliceCuotas {
  cuotasSelected: CuotasRenovacion;
  total: string | number | null;
  cantidadCuotasSelected: number;
  vigenciaDesde?: FechaRenovacion;
}

const useReduxSelector = () =>
  useSelector<any, SliceCuotas>((state) => {
    const detalle = state.renovacionPolizaReducer;
    return {
      cuotasSelected: detalle.cuotasSelected,
      total: detalle.premioTotalSelected.value,
      cantidadCuotasSelected: detalle.cantidadCuotasSelected.value,
    };
  });

const CuotasCard: React.FC<CuotasCardProps> = ({
  isLoading,
  displayError,
  onBlur,
}) => {
  const dispatch = useDispatch();

  const { total, cantidadCuotasSelected, cuotasSelected, vigenciaDesde } =
    useReduxSelector();

  useEffect(() => {
    let newImporteCuota = Number(total) / cantidadCuotasSelected;
    let newImportePrimerCuota = newImporteCuota;

    let decimals = newImporteCuota % 1;

    if (decimals !== 0) {
      newImportePrimerCuota = newImportePrimerCuota + decimals * 5;
      newImporteCuota = newImporteCuota - decimals;
    }

    for (let i = 1; i <= cantidadCuotasSelected; i++) {
      let fechaVtoCuota = moment(vigenciaDesde).add(i, "months");

      let importeCuota = i === 1 ? newImportePrimerCuota : newImporteCuota;

      const newCuota: CuotaRenovacion = {
        id: i,
        importe: {
          value: importeCuota,
          valid: true,
          focus: false,
        },
        fechaVencimiento: { value: fechaVtoCuota, valid: true, focus: false },
      };

      dispatch(setCuota(newCuota));
    }

    //eslint-disable-next-line
  }, [total]);

  const handleChangeCuota = (cuota: CuotaRenovacion) => {
    dispatch(setCuota(cuota));
    updateOthersCuotas(cuota);
  };

  const updateOthersCuotas = (cuota: CuotaRenovacion) => {
    const sumCuotasLoaded = Object.keys(cuotasSelected)
      .slice(0, cuota.id)
      .map((key) => parseFloat(String(cuotasSelected[key].importe.value)))
      .reduce((accumulator, value) => accumulator + value);

    const newImporte =
      (Number(total) - sumCuotasLoaded) / (cantidadCuotasSelected - cuota.id);

    Object.keys(cuotasSelected).map((key) => {
      const currentCuota = cuotasSelected[key];

      if (currentCuota.id > cuota.id) {
        const newCuota = {
          ...currentCuota,
          importe: { ...currentCuota.importe, value: newImporte },
        };

        dispatch(setCuota(newCuota));
        return newCuota;
      }

      return currentCuota;
    });

    Object.keys(cuotasSelected).map((key) => {
      const currentCuota = cuotasSelected[key];

      if (cuota.id === 1 && currentCuota.id !== cuota.id) {
        let newFechaVto = moment(cuota.fechaVencimiento.value).add(
          currentCuota.id - cuota.id,
          "months"
        );
        const newCuota = {
          ...currentCuota,
          fechaVencimiento: {
            ...currentCuota.fechaVencimiento,
            value: newFechaVto,
          },
        };

        dispatch(setCuota(newCuota));
        return newCuota;
      }

      return cuota;
    });
  };

  return (
    <DetalleCard isLoading={isLoading} title={title} IconHeader={ReceiptIcon}>
      {Object.keys(cuotasSelected).map((key) => (
        <ItemCuotaCard
          key={key}
          index={Number(key)}
          // La cuota del formulario guarda importes numéricos y fechas moment, y
          // no lleva vigencias: la card sólo lee esos campos en modo readOnly.
          cuota={cuotasSelected[key] as unknown as Cuota}
          onChange={(cuota) =>
            handleChangeCuota(cuota as unknown as CuotaRenovacion)
          }
          displayError={displayError}
          onBlur={onBlur}
          focus={
            cuotasSelected[key].importe.focus ||
            cuotasSelected[key].fechaVencimiento.focus
          }
        />
      ))}
    </DetalleCard>
  );
};

export default CuotasCard;
